import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  decodeText,
  fetchPreview,
  formatJson,
  parseCsv,
  readLimited,
} from './preview-content'
import { TEXT_PREVIEW_LIMIT_BYTES } from './preview-limits'
import type { CsvPreview, PreviewSource } from './preview-content'
import { DocumentPreviewRenderer } from '@/graphql/schema'

type Loaded = { text: string; truncated: boolean }

function useLoadedText(
  source: PreviewSource,
  onFailure: () => void,
): Loaded | null {
  const [loaded, setLoaded] = useState<Loaded | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    void (async () => {
      try {
        const response = await fetchPreview(source, controller.signal)
        const { bytes, truncated } = await readLimited(
          response,
          TEXT_PREVIEW_LIMIT_BYTES,
        )
        setLoaded({ text: decodeText(bytes), truncated })
      } catch {
        if (!controller.signal.aborted) onFailure()
      }
    })()

    return () => controller.abort()
  }, [source, onFailure])

  return loaded
}

function CsvTable({ preview }: { preview: CsvPreview }) {
  const { t } = useTranslation('documentPreview')
  const [header, ...body] = preview.rows

  return (
    <div>
      <table className="w-max min-w-full border-collapse text-left text-sm">
        <thead className="sticky top-0 bg-muted">
          <tr>
            {header.map((cell, column) => (
              <th
                key={column}
                scope="col"
                className="border px-2 py-1 font-medium"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, index) => (
            <tr key={index}>
              {row.map((cell, column) => (
                <td key={column} className="max-w-80 border px-2 py-1">
                  <span className="line-clamp-3 break-words">{cell}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {preview.rowsTruncated || preview.columnsTruncated ? (
        <p role="status" className="p-2 text-xs text-muted-foreground">
          {t('csvTruncated')}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Text formats are rendered as escaped React text, never as HTML, so embedded
 * markup or scripts in the file stay inert.
 */
export function TextPreview({
  source,
  renderer,
  onFailure,
}: {
  source: PreviewSource
  renderer: DocumentPreviewRenderer
  onFailure: () => void
}) {
  const { t } = useTranslation('documentPreview')
  const loaded = useLoadedText(source, onFailure)
  if (!loaded) return null
  if (renderer === DocumentPreviewRenderer.Csv) {
    const csv = parseCsv(loaded.text, loaded.truncated)
    if (csv.rows.length)
      return (
        <div
          tabIndex={0}
          role="region"
          aria-label={t('contentRegion')}
          className="size-full overflow-auto"
        >
          <CsvTable preview={csv} />
        </div>
      )
  }
  const text =
    renderer === DocumentPreviewRenderer.Json
      ? formatJson(loaded.text, loaded.truncated)
      : loaded.text

  return (
    <div
      tabIndex={0}
      role="region"
      aria-label={t('contentRegion')}
      className="size-full overflow-auto"
    >
      <pre className="p-3 font-mono text-sm break-words whitespace-pre-wrap">
        {text}
      </pre>
      {loaded.truncated ? (
        <p role="status" className="p-3 text-xs text-muted-foreground">
          {t('textTruncated')}
        </p>
      ) : null}
    </div>
  )
}
